import { ImageOff, Loader2, Play } from "lucide-react";
import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { canWrite, useRole } from "@/features/moderation/hooks";
import { api } from "@/lib/api/client";
import { liveEvents } from "@/lib/api/events";
import { invalidate } from "@/lib/api/query-client";
import { useT } from "@/lib/i18n";
import type { MediaAsset } from "@/lib/types";
import { cn } from "@/lib/utils";
import { isVideo } from "./lib";

/**
 * Channel media (ALP-77 media fix). Imported posts show OUR stored copy; until it exists the API sends the Instagram CDN
 * URL (`mirrored: false`), which expires within days. When an image fails to load we show an accessible placeholder and,
 * once per post per session (editors and up), ask the API for fresh media (`refreshPostMedia` → a media_refresh job);
 * when that job ends the post caches are refetched and the new URL renders (the <img> is keyed on its URL).
 */

// ------------------------------------------------------------------ refresh requests (module state: one per post per session)
const requested = new Set<string>();
const refreshing = new Set<string>();
const subscribers = new Set<() => void>();
let version = 0;
const notify = () => {
  version++;
  subscribers.forEach((l) => l());
};
const subscribe = (l: () => void) => {
  subscribers.add(l);
  return () => void subscribers.delete(l);
};

/** Test helper: forget which posts were already refreshed this session. */
export function resetMediaRefreshState() {
  requested.clear();
  refreshing.clear();
  notify();
}

export function isRefreshingMedia(postId: string | null | undefined) {
  return !!postId && refreshing.has(postId);
}

const done = (postId: string) => {
  refreshing.delete(postId);
  notify();
  void invalidate("posts", "post", "postVersions", "inbox", "commentDetail");
};

/**
 * Ask the API to fetch fresh media for a post and re-copy it (idempotent server-side: one job in flight per post).
 * Returns false when this post was already refreshed in this session (never loops on a permanently broken image).
 */
export function requestPostMediaRefresh(postId: string): boolean {
  if (requested.has(postId)) return false;
  requested.add(postId);
  refreshing.add(postId);
  notify();
  void api("refreshPostMedia", { params: { postId }, body: {} })
    .then((job) => {
      if (job.status === "succeeded" || job.status === "failed" || job.status === "cancelled") return done(postId);
      const off = liveEvents.on((e) => {
        if (e.type !== "operation.updated" || e.operationId !== job.id) return;
        if (e.status === "succeeded" || e.status === "failed" || e.status === "cancelled") {
          off();
          clearTimeout(timer);
          done(postId);
        }
      });
      // the SSE stream may be down: never spin forever
      const timer = setTimeout(() => {
        off();
        done(postId);
      }, 90_000);
    })
    .catch(() => {
      refreshing.delete(postId);
      notify();
    });
  return true;
}

function useRefreshing(postId: string | null | undefined) {
  useSyncExternalStore(subscribe, () => version, () => version);
  return isRefreshingMedia(postId);
}

// ------------------------------------------------------------------ component
export interface ChannelImageProps {
  /** The media asset (its url, kind/mime for the video badge). */
  media?: Pick<MediaAsset, "url" | "mime"> & Partial<Pick<MediaAsset, "kind" | "mirrored" | "altText">> | null;
  /** Or a bare URL (post refs carry only a thumbnail URL). */
  src?: string | null;
  /** The post the media belongs to: a broken image asks a refresh for it. */
  postId?: string | null;
  alt?: string;
  className?: string;
  imgClassName?: string;
  /** Small thumbnails: icon-only placeholder, no video badge text. */
  compact?: boolean;
  /** Show a play badge on videos (default true when not compact). */
  videoBadge?: boolean;
  loading?: "lazy" | "eager";
  draggable?: boolean;
  /** Rendered over the image (status badges etc.). */
  children?: ReactNode;
  testId?: string;
}

export function ChannelImage({ media, src, postId, alt = "", className, imgClassName, compact, videoBadge, loading = "lazy", draggable, children, testId }: ChannelImageProps) {
  const { t } = useT();
  const role = useRole();
  const url = media?.url ?? src ?? null;
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  const isRefreshing = useRefreshing(postId);
  const broken = !!url && brokenUrl === url;
  const video = !!media && (media.kind === "video" || isVideo(media.mime));
  const showBadge = videoBadge ?? !compact;

  const onError = () => {
    if (url) setBrokenUrl(url);
  };
  // the role may still be loading when the image fails: ask once it is known (editors and up; viewers see the placeholder)
  const mayRefresh = canWrite(role);
  useEffect(() => {
    if (broken && postId && mayRefresh) requestPostMediaRefresh(postId);
  }, [broken, postId, mayRefresh]);

  return (
    <span className={cn("relative block overflow-hidden bg-surface-2", className)} data-testid={testId} data-media-state={!url ? "empty" : broken ? (isRefreshing ? "refreshing" : "broken") : "ok"}>
      {url && !broken && (
        <img
          key={url}
          src={url}
          alt={alt || media?.altText || ""}
          loading={loading}
          draggable={draggable}
          onError={onError}
          className={cn("size-full object-cover", imgClassName)}
          data-mirrored={media?.mirrored === false ? "false" : undefined}
        />
      )}
      {(broken || !url) && (
        <span role="img" aria-label={isRefreshing ? t("media.image.refreshing") : t("media.image.unavailable")} className="absolute inset-0 grid place-items-center text-ink-3" data-testid="media-placeholder">
          <span className="flex flex-col items-center gap-1 px-2 text-center">
            {isRefreshing ? <Loader2 className={cn("animate-spin motion-reduce:animate-none", compact ? "size-3.5" : "size-6")} aria-hidden /> : <ImageOff className={compact ? "size-3.5" : "size-6"} aria-hidden />}
            {!compact && <span className="text-[11.5px] font-medium">{isRefreshing ? t("media.image.refreshing") : t("media.image.unavailable")}</span>}
          </span>
        </span>
      )}
      {video && showBadge && url && !broken && (
        <span className="pointer-events-none absolute inset-0 grid place-items-center">
          <span className="grid size-11 place-items-center rounded-full bg-black/45 text-white backdrop-blur">
            <Play className="size-5" aria-hidden />
            <span className="sr-only">{t("media.image.video")}</span>
          </span>
        </span>
      )}
      {children}
    </span>
  );
}

/** "Copying media…" chip for posts whose media are still being copied into storage. */
export function MediaCopyingChip({ status, className }: { status: string | undefined; className?: string }) {
  const { t } = useT();
  if (status !== "pending") return null;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[10.5px] font-medium text-white", className)} title={t("media.image.copyingHint")} data-testid="media-copying">
      <Loader2 className="size-3 animate-spin motion-reduce:animate-none" aria-hidden />
      {t("media.image.copying")}
    </span>
  );
}
